import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getWaterUsageList,
  addWaterUsageToProduct,
  updateWaterUsage,
  deleteWaterUsage,
  getWaterUsageCountryData,
  getWaterUsageSuppliersDropdown,
  getWaterMeasurementUnits,
} from "../../../../../../apis/Product/WaterUsageApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import WaterSupplierAutocomplete from "../../../../../../components/WaterSupplierAutocomplete";
import QuantityWithUnit from "../../../../../../components/QuantityWithUnit";
import EmissionFactorWithUnit from "../../../../../../components/EmissionFactorWithUnit";

const ProdWaterUsage = ({ productId, method = "emission_factor" }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get("business");

  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownCountries, setDropdownCountries] = useState([]);
  const [dropdownSuppliers, setDropdownSuppliers] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [units, setUnits] = useState({
    quantityUnits: [],
    emissionFactorUnits: [],
  });

  // const [dropdownUsedFor, setDropdownUsedFor] = useState([
  //   {
  //     id: 1,
  //     name: t("DRINKING_WATER_EUROPE"),
  //     value: t("DRINKING_WATER_EUROPE"),
  //     type: t("DRINKING_WATER_EUROPE"),
  //     unit: "kg",
  //     typeDB: "europe_drinking_water",
  //     key: "DRINKING_WATER_EUROPE",
  //   },
  //   {
  //     id: 2,
  //     name: t("INDUSTRIAL_REVERSE_OSMOSIS"),
  //     value: t("INDUSTRIAL_REVERSE_OSMOSIS"),
  //     type: t("INDUSTRIAL_REVERSE_OSMOSIS"),
  //     unit: "kg",
  //     typeDB: "osmosis",
  //     key: "INDUSTRIAL_REVERSE_OSMOSIS",
  //   },
  // ]);

  // Dynamic currency options based on selected country
  const currencyOptions = useMemo(() => {
    if (!selectedCountry) return [];

    return [
      {
        value: selectedCountry.currency_name,
        id: selectedCountry.currency_code,
        currency_code: selectedCountry.currency_code,
        currency_name: selectedCountry.currency_name,
        exchange_rate: selectedCountry.exchange_rate,
        inflation_rate: selectedCountry.inflation_rate,
        emission_factor: selectedCountry.emission_factor,
      },
    ];
  }, [selectedCountry]);

  // Dynamic placeholders for country to currency mapping
  const dynamicPlaceholders = useMemo(
    () => ({
      country: {
        targetField: "currency",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedCountry = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedCountry) {
            setSelectedCountry(selectedCountry);
            return {
              value: selectedCountry.currency_name,
              id: selectedCountry.currency_code,
              currency_code: selectedCountry.currency_code,
              currency_name: selectedCountry.currency_name,
              exchange_rate: selectedCountry.exchange_rate,
              inflation_rate: selectedCountry.inflation_rate,
              emission_factor: selectedCountry.emission_factor,
            };
          }
          return null;
        },
        dropdownDataKey: "country",
      },
    }),
    []
  );

  useEffect(() => {
    getWaterUsageCountryData({
      setDropdown: setDropdownCountries,
    });

    // Fetch water suppliers for dropdown if businessId is available
    if (businessId) {
      getWaterUsageSuppliersDropdown({
        businessId,
        setDropdown: setDropdownSuppliers,
      });
    }

    // Fetch water measurement units
    getWaterMeasurementUnits({
      setUnits: setUnits,
    });
  }, [businessId]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const getFieldValue = useCallback((field) => {
    if (!field) return null;
    if (typeof field === "object") {
      // For dropdown fields, use the id if available, otherwise use value
      return field.id || field.value;
    }
    return field;
  }, []);

  const getUnitFieldValue = useCallback((field, unitField) => {
    if (!field) return { value: null, unit: null };
    if (typeof field === "object") {
      // For unit fields, extract both value and unit
      return {
        value: field.qty || field.manuel || field.value || null,
        unit: field.qty_unit || field.manuel_unit || field.unit || null,
      };
    }
    return { value: field, unit: unitField };
  }, []);

  const fetchSuppliers = useCallback(() => {
    if (businessId) {
      getWaterUsageSuppliersDropdown({
        businessId,
        setDropdown: setDropdownSuppliers,
      });
    }
  }, [businessId]);

  // Water type options for both methods
  const waterTypeOptions = useMemo(
    () => [
      { value: "freshwater", label: t("FRESHWATER") || "Freshwater" },
      { value: "well water", label: t("WELL_WATER") || "Well Water" },
      { value: "wastewater", label: t("WASTEWATER") || "Wastewater" },
      { value: "rainwater", label: t("RAINWATER") || "Rainwater" },
      {
        value: "drainage water",
        label: t("DRAINAGE_WATER") || "Drainage Water",
      },
      { value: "melt water", label: t("MELT_WATER") || "Melt Water" },
    ],
    [t]
  );

  // Function to convert identifier back to display object for UI
  const convertIdentifierToDisplay = useCallback(
    (field, fieldName) => {
      if (!field) return null;

      if (fieldName === "currency") {
        const option = currencyOptions.find((item) => item.id === field);
        return option ? { value: option.value, id: option.id } : field;
      }

      if (fieldName === "water_type") {
        const option = waterTypeOptions.find((item) => item.value === field);
        return option ? { value: option.value, label: option.label } : field;
      }

      if (fieldName === "water_supplier") {
        const option = dropdownSuppliers.find((item) => item.value === field);
        return option ? { value: option.value, label: option.label } : field;
      }

      return field;
    },
    [currencyOptions, waterTypeOptions, dropdownSuppliers]
  );

  const fetchData = useCallback(() => {
    getWaterUsageList({
      productId,
      setObjects: (fetchedData) => {
        // Filter data by method
        const filteredData = fetchedData.filter(
          (item) => item.method === method
        );

        // Convert identifiers back to display objects for UI
        const convertedData = filteredData.map((item) => ({
          ...item,
          water_type: convertIdentifierToDisplay(item.water_type, "water_type"),
          water_supplier: convertIdentifierToDisplay(
            item.water_supplier,
            "water_supplier"
          ),
          currency: convertIdentifierToDisplay(item.currency, "currency"),
          qty: {
            qty: item.qty,
            qty_unit: item.qty_unit || "L",
          },
          manuel: {
            manuel: item.manuel,
            manuel_unit: item.manuel_unit || "kgCO₂/L",
          },
        }));

        setData(convertedData);
      },
    });
  }, [productId, method, convertIdentifierToDisplay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      // Find the original item to get the current currency if not changed
      const originalItem = data.find((item) => item.id === editedItem.id);

      // If currency is not in editedItem (user didn't touch currency field), use original
      let currencyValue = null;
      if (editedItem.hasOwnProperty("currency")) {
        // User edited currency field
        currencyValue =
          editedItem.currency?.currency_code || editedItem.currency?.id || null;
      } else {
        // User didn't touch currency field, use original
        currencyValue =
          originalItem?.currency?.currency_code ||
          originalItem?.currency?.id ||
          null;
      }

      // For invoice method, currency is required
      if (method === "invoice" && !currencyValue) {
        console.error("Currency is required for invoice method", {
          editedItem,
          originalItem,
          currencyValue,
          method,
        });
        return;
      }

      // Handle unit fields for emission factor method
      const qtyData = getUnitFieldValue(editedItem.qty);
      const manuelData = getUnitFieldValue(editedItem.manuel);

      updateWaterUsage({
        productId,
        componentId: editedItem.id,
        method: method,
        ...editedItem,
        water_type: getFieldValue(editedItem.water_type),
        water_supplier: getFieldValue(editedItem.water_supplier),
        country: getFieldValue(editedItem.country),
        currency: currencyValue,
        qty: qtyData.value,
        qty_unit: qtyData.unit,
        manuel: manuelData.value,
        manuel_unit: manuelData.unit,
        price_per_functional_unit: getFieldValue(
          editedItem.price_per_functional_unit
        ),
        objects: data,
        setObjects: setData,
        callback: fetchSuppliers, // Refresh suppliers after editing
        use_swal: true,
      });
    },
    [
      productId,
      method,
      data,
      setData,
      fetchSuppliers,
      getFieldValue,
      getUnitFieldValue,
    ]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const currencyValue =
        newItem.currency?.currency_code || newItem.currency?.id || null;

      // For invoice method, currency is required
      if (method === "invoice" && !currencyValue) {
        console.error("Currency is required for invoice method", {
          newItem,
          currencyValue,
          method,
        });
        return;
      }

      // Handle unit fields for emission factor method
      const qtyData = getUnitFieldValue(newItem.qty);
      const manuelData = getUnitFieldValue(newItem.manuel);

      addWaterUsageToProduct({
        productId,
        method: method,
        ...newItem,
        water_type: getFieldValue(newItem.water_type),
        water_supplier: getFieldValue(newItem.water_supplier),
        country: getFieldValue(newItem.country),
        currency: currencyValue,
        qty: qtyData.value,
        qty_unit: qtyData.unit,
        manuel: manuelData.value,
        manuel_unit: manuelData.unit,
        price_per_functional_unit: getFieldValue(
          newItem.price_per_functional_unit
        ),
        callback: () => {
          fetchData();
          fetchSuppliers(); // Refresh suppliers after adding new item
        },
        use_swal: true,
      });
    },
    [
      productId,
      method,
      fetchData,
      fetchSuppliers,
      getFieldValue,
      getUnitFieldValue,
    ]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWaterUsage({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  // Flow 11.3.2: Emission Factor Method columns
  const emissionFactorColumns = useMemo(
    () => [
      {
        accessor: "water_type",
        header: `${t("TYPE_WATER")}*`,
        type: "dropdown",
        dropdownData: waterTypeOptions,
        disableSortBy: true,
        width: "150px",
        tipHeader: "Tip",
        tipBody:
          t("WATER_TYPE_TIP") ||
          "We use the type of water for documentation and statistical purposes. The conversion between units and the associated emission factor is based purely on freshwater.",
      },
      {
        accessor: "water_supplier",
        header: `${t("WATER_SUPPLIER")}*`,
        type: "custom",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "qty",
        header: `${t("QTY_FUNC_UNIT")}*`,
        type: "custom",
        tipHeader: "Tip",
        tipBody: t("TIP_QTY_FUNC_WATER"),
        disableSortBy: true,
        width: "180px",
      },
      {
        accessor: "manuel",
        header: `${t("MANUAL_EMISSION_FACTOR")}*`,
        type: "custom",
        tipHeader: "Tip",
        tipBody: t("TIP_MANUEL_PROD"),
        disableSortBy: true,
        width: "180px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [waterTypeOptions, t]
  );

  // Flow 11.3.1: Invoice Method columns
  const invoiceColumns = useMemo(
    () => [
      {
        accessor: "water_type",
        header: `${t("TYPE_WATER")}*`,
        type: "dropdown",
        dropdownData: waterTypeOptions,
        disableSortBy: true,
        width: "150px",
        tipHeader: "Tip",
        tipBody:
          t("WATER_TYPE_TIP") ||
          "We use the type of water for documentation and statistical purposes. The conversion between units and the associated emission factor is based purely on freshwater.",
      },
      {
        accessor: "water_supplier",
        header: `${t("WATER_SUPPLIER")}*`,
        type: "custom",
        disableSortBy: true,
        width: "150px",
      },
      {
        accessor: "country",
        header: `${t("COUNTRY")} *`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "120px",
      },
      {
        accessor: "currency",
        header: `${t("CURRENCY")} *`,
        type: "dropdown",
        dropdownData: currencyOptions,
        disableSortBy: true,
        width: "100px",
        disabled: !selectedCountry,
      },
      {
        accessor: "price_per_functional_unit",
        header: (props) => {
          const currency = props.row?.original?.currency;
          const currencyCode = currency?.currency_code || currency?.id || "EUR";
          return `${t("PRICE_PER_INVOICE")}* (${currencyCode})`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [
      waterTypeOptions,
      dropdownSuppliers,
      dropdownCountries,
      currencyOptions,
      selectedCountry,
      t,
    ]
  );

  // Select columns based on method
  const columns = useMemo(() => {
    return method === "invoice" ? invoiceColumns : emissionFactorColumns;
  }, [method, invoiceColumns, emissionFactorColumns]);

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      water_type: waterTypeOptions,
      water_supplier: dropdownSuppliers,
      country: dropdownCountries,
      currency: currencyOptions,
    }),
    [waterTypeOptions, dropdownSuppliers, dropdownCountries, currencyOptions]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{
        carbon: CustomInputComponent,
        water_supplier: (props) => (
          <WaterSupplierAutocomplete
            {...props}
            options={dropdownSuppliers}
            placeholder={t("ENTER_SUPPLIER_NAME") || "Enter supplier name"}
          />
        ),
        qty: (props) => (
          <QuantityWithUnit
            {...props}
            units={units.quantityUnits}
            placeholder="0"
            defaultUnit="L"
          />
        ),
        manuel: (props) => (
          <EmissionFactorWithUnit
            {...props}
            units={units.emissionFactorUnits}
            placeholder="0"
            defaultUnit="kgCO₂/L"
          />
        ),
      }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdWaterUsage;
